import type { ReactNode } from 'react';
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * The phone's bottom sheet. Two flavours:
 *
 * - plain: sits over the lower part of the screen while the canvas above it
 *   stays visible and usable (colour, details, the tool panels) and is
 *   confirmed with a check mark;
 * - modal: dims everything behind it and is dismissed with a cross (size and
 *   purchase, product catalogue) — the customer is deciding, not designing.
 *
 * Only ever rendered below 981px; the desktop keeps the mock's drawers.
 */
export function DirectSaleSheet({
  title,
  subtitle,
  onClose,
  modal = false,
  tall = false,
  headerAction,
  children,
}: {
  title: string;
  subtitle?: string | null;
  onClose: () => void;
  modal?: boolean;
  /** Nearly full height, for content the customer browses (the catalogue). */
  tall?: boolean;
  headerAction?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();

  const sheet = (
    <div
      role="dialog"
      aria-label={title}
      aria-modal={modal}
      className={cn(
        'flex w-full flex-col rounded-t-[20px] bg-paper shadow-[0_-6px_20px_rgba(13,13,13,0.12)]',
        tall ? 'h-[94dvh]' : 'max-h-[62dvh]',
        !modal && 'fixed inset-x-0 bottom-0 z-30'
      )}
    >
      <div className="flex flex-none items-center pl-[18px] pr-2 pt-2">
        <div className="flex min-w-0 flex-1 items-baseline gap-2.5">
          <h2 className="font-display text-[19px] font-bold text-ink">
            {title}
          </h2>
          {subtitle && (
            <span className="truncate text-sm text-ink-3">{subtitle}</span>
          )}
        </div>
        {headerAction}
        <button
          type="button"
          aria-label={
            modal ? t('directSale.design.close') : t('directSale.mobile.done')
          }
          onClick={onClose}
          className="flex h-12 w-12 flex-none items-center justify-center text-ink"
        >
          {modal ? (
            <X className="h-6 w-6" strokeWidth={2.2} />
          ) : (
            <Check className="h-[26px] w-[26px]" strokeWidth={2.4} />
          )}
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[max(env(safe-area-inset-bottom),12px)]">
        {children}
      </div>
    </div>
  );

  if (!modal) return sheet;

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-black/50">
      <button
        type="button"
        aria-label={t('directSale.design.close')}
        onClick={onClose}
        className="min-h-0 flex-1 cursor-default"
      />
      {sheet}
    </div>
  );
}
